// The app shell: the left rail plus a content slot.
//
// This markup started life inside StudioNext, where it was the Studio's own
// chrome. It's lifted out here because the rail is now the navigation for every
// signed-in page, replacing the old TopBar — so the brand, account menu, plan
// card and theme switch stay put as you move between Studio, Library, Settings
// and the rest, instead of the whole frame changing under you.
//
// The rail's *contents* are the caller's business: pass `groups`. The Studio
// passes screen-switching buttons; other pages pass route links.
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { useAuth } from '../lib/auth';
import { useRailCollapsed } from '../lib/railState';
// Namespaced `sx-` / `sxn-` / `sxr` / `sxm`; every other page uses its own
// prefix, so loading it app-wide can't reach them.
import '../pages/studio-next.css';

export interface RailItem {
  id: string;
  /** SVG path data, drawn at 17px with a 1.8 stroke. */
  icon: string;
  label: string;
  badge?: string;
  /** Route to link to. Ignored when `onClick` is given. */
  to?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  /** Tooltip; falls back to the label (the only text a collapsed rail shows). */
  title?: string;
}

export interface RailGroup {
  /** Section heading, shown only when the rail is expanded. */
  label: string;
  items: RailItem[];
}

function PanelIcon({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <line x1="9" y1="4" x2="9" y2="20" />
    </svg>
  );
}

function Ico({ d, size = 18, sw = 1.9 }: { d: string; size?: number; sw?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark',
  );
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('rl-theme', next); } catch { /* ignore */ }
  };
  return { theme, toggle };
}

export function AppShell({
  groups,
  children,
  rootClass = '',
  quota,
}: {
  groups: RailGroup[];
  children: ReactNode;
  /** Extra class on the shell root (the Studio adds `drawer-open`). */
  rootClass?: string;
  /** Free-plan counter for the rail card. Omit and the card shows plan only. */
  quota?: { remaining: number | null; dailyQuota: number };
}) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useRailCollapsed();
  const navOpen = !collapsed;
  const { theme, toggle: toggleTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Phones hide the rail entirely, so the account block gets a second home in
  // the mobile bar below. Separate state: the two are never on screen at once,
  // but sharing one flag would have a rotated phone open the rail's menu.
  const [acctSheetOpen, setAcctSheetOpen] = useState(false);
  const acctSheetRef = useRef<HTMLDivElement>(null);
  const isPro = user?.plan === 'pro';

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  // `pointerdown`, not `mousedown`: the synthesized mouse event only lands
  // after a touch is released, so on a phone the sheet stayed up under your
  // finger for the length of the tap.
  useEffect(() => {
    if (!acctSheetOpen) return;
    const onDown = (e: PointerEvent) => {
      if (acctSheetRef.current && !acctSheetRef.current.contains(e.target as Node)) setAcctSheetOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [acctSheetOpen]);

  const renderItem = (n: RailItem) => {
    const label = n.title || n.label;
    const inner = (
      <>
        <span className="sxn-ico"><Ico d={n.icon} size={17} sw={1.8} /></span>
        {navOpen && <span className="sxn-l">{n.label}</span>}
        {n.badge && <span className="sxn-b">{n.badge}</span>}
        {/* Collapsed, the icon is all there is, so it carries the same styled
            bubble the expand button and the crown use. The native `title` is
            dropped alongside it — left on, the OS tooltip fades in a second
            later and you end up reading the label twice, in two designs. */}
        {!navOpen && <span className="sxn-tip">{label}</span>}
      </>
    );
    const cls = `sxn-i${n.active ? ' on' : ''}`;
    // The bubble isn't an accessible name, so the icon-only state still needs
    // one spelled out.
    const a11y = navOpen ? { title: label } : { 'aria-label': label };
    // A route link stays a real anchor — middle-click and "open in new tab"
    // are worth more than markup symmetry.
    if (n.to && !n.onClick && !n.disabled) {
      return (
        <Link key={n.id} className={cls} to={n.to} {...a11y}>
          {inner}
        </Link>
      );
    }
    return (
      <button
        key={n.id}
        className={cls}
        onClick={() => !n.disabled && n.onClick?.()}
        disabled={n.disabled}
        {...a11y}
      >
        {inner}
      </button>
    );
  };

  // The sheet always has room for a label, so it can't reuse `renderItem` —
  // that one keys off `navOpen`, and a rail collapsed on a desktop would
  // follow you onto a phone as a column of unlabelled glyphs.
  const renderSheetItem = (n: RailItem) => {
    const inner = (
      <>
        <span className="sxn-ico"><Ico d={n.icon} size={17} sw={1.8} /></span>
        <span className="sxn-l">{n.label}</span>
        {n.badge && <span className="sxn-b">{n.badge}</span>}
      </>
    );
    const cls = `sxn-i${n.active ? ' on' : ''}`;
    if (n.to && !n.onClick && !n.disabled) {
      return (
        <Link key={n.id} className={cls} to={n.to} onClick={() => setAcctSheetOpen(false)}>
          {inner}
        </Link>
      );
    }
    return (
      <button
        key={n.id}
        className={cls}
        disabled={n.disabled}
        title={n.disabled ? n.title : undefined}
        onClick={() => { if (n.disabled) return; setAcctSheetOpen(false); n.onClick?.(); }}
      >
        {inner}
      </button>
    );
  };

  const avatar = user?.picture_url ? (
    <img
      className="sxn-avatar-img"
      src={user.picture_url}
      alt=""
      referrerPolicy="no-referrer"
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
    />
  ) : (
    <span className="sxn-avatar">{user?.name?.[0]?.toUpperCase() ?? '?'}</span>
  );

  return (
    <div className={`sxr${navOpen ? '' : ' nav-collapsed'}${rootClass ? ` ${rootClass}` : ''}`}>
      <nav className="sxn">
        {/* Only the nav lists scroll. The theme switch and the account block are
            siblings of this box, never inside it, so a short window can never
            push the profile out of the rail. */}
        <div className="sxn-scroll">
          <div className="sxn-top">
            {navOpen ? (
              <>
                <span className="sxn-logo"><Logo size="sm" linkTo="/dashboard" /></span>
                <button className="sxn-collapse" onClick={() => setCollapsed(true)} title="Collapse" aria-label="Collapse navigation">
                  <PanelIcon />
                </button>
              </>
            ) : (
              /* Collapsed, the logo IS the control — it swaps to the panel glyph
                 on hover and explains itself, so a 76px rail doesn't need a
                 second button competing with the mark. */
              <button className="sxn-expand" onClick={() => setCollapsed(false)} aria-label="Expand navigation">
                <span className="sxn-expand-logo"><Logo size="sm" variant="icon" /></span>
                <span className="sxn-expand-ico"><PanelIcon size={19} /></span>
                <span className="sxn-tip">Click to expand navigation</span>
              </button>
            )}
          </div>

          {groups.map((g) => (
            <div key={g.label}>
              {/* An empty label means a standalone item that belongs to no
                  section — Dashboard sits above the first heading rather than
                  under "Products", which it isn't one of. Guarded so the
                  heading's own margin doesn't leave a gap where no text is. */}
              {navOpen && g.label && <div className="sxn-sec">{g.label}</div>}
              <div className="sxn-list">{g.items.map(renderItem)}</div>
            </div>
          ))}

          {navOpen && (
            <div className="sxn-quota">
              {isPro ? (
                <>
                  <div className="sxn-quota-t">★ Pro</div>
                  <div className="sxn-quota-s">Unlimited separations. Looping is always free.</div>
                </>
              ) : (
                <>
                  {/* Only the Studio knows the day's count. Elsewhere, show the
                      plan and the upgrade path rather than an em-dash where a
                      number should be — a wrong-looking counter is worse than
                      no counter. */}
                  <div className="sxn-quota-t">
                    {quota ? `${quota.remaining ?? '—'} of ${quota.dailyQuota} left` : 'Free plan'}
                  </div>
                  {quota && (
                    <div className="sxn-meter">
                      <i style={{ width: `${Math.round(((quota.remaining ?? 0) / (quota.dailyQuota || 5)) * 100)}%` }} />
                    </div>
                  )}
                  <div className="sxn-quota-s">Free separations reset every day. Looping is always free.</div>
                  <Link className="sxn-quota-cta" to="/pricing">Upgrade Plan</Link>
                </>
              )}
            </div>
          )}
        </div>

        {/* Collapsed, the quota card can't fit — free users keep the upgrade
            path as a crown. */}
        {!navOpen && !isPro && (
          <Link className="sxn-crown" to="/pricing" aria-label="Upgrade Plan">
            <Ico d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" size={20} sw={1.7} />
            <span className="sxn-tip">Upgrade Plan</span>
          </Link>
        )}

        <div className="sxn-foot">
          <button className="sxn-theme" onClick={toggleTheme} title={navOpen ? 'Toggle theme' : undefined} aria-label="Toggle theme">
            {theme === 'dark' ? '☀' : '☾'}
            {!navOpen && <span className="sxn-tip">Toggle theme</span>}
          </button>
          {navOpen && <span className="sxn-foot-l">{theme === 'dark' ? 'Dark' : 'Light'}</span>}
        </div>

        {/* Opens upward — this block sits at the bottom of the rail. */}
        <div className="sxn-userwrap" ref={userMenuRef}>
          {userMenuOpen && (
            <div className="sxn-menu" role="menu">
              <div className="sxn-menu-head">
                <div className="sxn-menu-n">{user?.name}</div>
                <div className="sxn-menu-e">{user?.email}</div>
                <span className={`sxn-menu-plan${isPro ? ' pro' : ''}`}>{user?.plan}</span>
              </div>
              <Link className="sxn-menu-i" to="/settings" onClick={() => setUserMenuOpen(false)}>Settings</Link>
              <Link className="sxn-menu-i" to="/billing" onClick={() => setUserMenuOpen(false)}>Billing</Link>
              <Link className="sxn-menu-i" to="/support" onClick={() => setUserMenuOpen(false)}>Support</Link>
              <button className="sxn-menu-i" onClick={() => { setUserMenuOpen(false); void logout(); }}>Sign out</button>
            </div>
          )}
          <button
            className="sxn-user"
            onClick={() => setUserMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={navOpen ? user?.email || 'Account' : undefined}
            aria-label={navOpen ? undefined : user?.email || 'Account'}
          >
            {!navOpen && !userMenuOpen && <span className="sxn-tip">{user?.email || 'Account'}</span>}
            {avatar}
            {navOpen && (
              <span className="sxn-user-txt">
                <span className="sxn-user-n">{user?.name}</span>
                <span className="sxn-user-p">{isPro ? 'Pro plan' : 'Free plan'}</span>
              </span>
            )}
            {navOpen && <span className="sxn-user-dots"><Ico d="M12 5.5h.01M12 12h.01M12 18.5h.01" size={17} sw={2.4} /></span>}
          </button>
        </div>
      </nav>

      <main className="sxm">
        {/* Under 860px the rail is `display: none`, and everything it holds went
            with it — the account menu, the theme switch, the upgrade path and
            the nav itself. The Studio's bottom tabs only switch its own three
            screens, and the other pages have no tabs at all, so a phone had no
            way to reach Settings, Billing, Dashboard — or Sign out.

            A flex sibling above the scrolling body, not a fixed overlay: it
            costs its height exactly once and can't come to rest on top of the
            tab bar or the player. */}
        <div className="sxn-mtop" ref={acctSheetRef}>
          <span className="sxn-mtop-brand"><Logo size="sm" linkTo="/dashboard" /></span>
          <button
            className="sxn-mtop-user"
            onClick={() => setAcctSheetOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={acctSheetOpen}
            aria-label={user?.email || 'Account'}
          >
            {avatar}
            <Ico d="M6 9.5l6 6 6-6" size={15} sw={2.2} />
          </button>

          {acctSheetOpen && (
            <div className="sxn-msheet" role="menu">
              <div className="sxn-menu-head">
                <div className="sxn-menu-n">{user?.name}</div>
                <div className="sxn-menu-e">{user?.email}</div>
                <span className={`sxn-menu-plan${isPro ? ' pro' : ''}`}>{user?.plan}</span>
              </div>

              {/* The rail's own nav, verbatim. Without it a phone on /settings
                  or /billing is a dead end — those pages render no tabs. */}
              {groups.map((g) => (
                <div key={g.label}>
                  {g.label && <div className="sxn-sec">{g.label}</div>}
                  <div className="sxn-list">{g.items.map(renderSheetItem)}</div>
                </div>
              ))}

              <div className="sxn-msheet-rule" />

              {!isPro && (
                <Link className="sxn-msheet-cta" to="/pricing" onClick={() => setAcctSheetOpen(false)}>
                  Upgrade Plan
                </Link>
              )}
              <Link className="sxn-menu-i" to="/settings" onClick={() => setAcctSheetOpen(false)}>Settings</Link>
              <Link className="sxn-menu-i" to="/billing" onClick={() => setAcctSheetOpen(false)}>Billing</Link>
              <Link className="sxn-menu-i" to="/support" onClick={() => setAcctSheetOpen(false)}>Support</Link>
              {/* Stays open — the point of the row is watching the theme land. */}
              <button className="sxn-menu-i sxn-msheet-row" onClick={toggleTheme}>
                <span>Theme</span>
                <b>{theme === 'dark' ? '☀ Light' : '☾ Dark'}</b>
              </button>
              <button className="sxn-menu-i" onClick={() => { setAcctSheetOpen(false); void logout(); }}>Sign out</button>
            </div>
          )}
        </div>

        {children}
      </main>
    </div>
  );
}
